// Haagse bezonningsnorm-toets (zie §1.1 / §2.5).
//
// Methodiek (gangbare interpretatie):
//   - Lichte toetsing: 19 februari én 21 oktober — minimaal 2 uur
//     ononderbroken zon op het meetpunt.
//   - Strenge toetsing: 19 januari én 22 november — minimaal 3 uur
//     ononderbroken zon op het meetpunt (strengere drempel én strengere
//     datums; de zon staat in januari/november nog lager dan in
//     februari/oktober).
//   - Toets-window: 10:00–18:00 NL-kloktijd.
//
// We pakken als kritiek meetpunt het zwaartepunt (centroid) van het
// betreffende meetvlak — een pragmatische keuze die voor een scherp gesprek
// volstaat (een echt rapport zou meerdere meetpunten gebruiken).
//
// Voor elke toetsdatum sampelen we tussen 10:00 en 18:00 in stappen van
// 2 minuten en bepalen of het meetpunt op dat moment in volle zon ligt
// (geen ondoorlatend blok blokkeert de zon-straal). De langste
// aaneengesloten reeks zon-samples is de "ononderbroken zonperiode".
//
// Belangrijk: voor de Haagse norm tellen we transparante blokken
// (boombladdek met < 100% blocking) als wel-blokkerend conform het
// gangbare rapport-uitgangspunt. Voor v1 zou je dit later kunnen versoepelen.

import type { Block, Scenario } from "../types/scenario";
import { detectMeasurementSurfaces } from "./grid";
import { computeShadowsAt, MIN_ELEVATION_RAD } from "./projectShadow";
import { getSunPosition, nlClockToUtc } from "./sunPosition";
import { pointInPolygon } from "../utils/geo";

const REFERENCE_YEAR = 2025;
const SAMPLE_STEP_MIN = 2;
const WINDOW_START_MIN = 10 * 60; // 10:00
const WINDOW_END_MIN = 18 * 60; // 18:00

const REQUIRED_MINUTES: Record<"licht" | "streng", number> = {
  licht: 120, // 2 uur
  streng: 180, // 3 uur
};

interface CheckDate {
  label: string;
  dateLabel: string;
  /** 0-based maand */
  month: number;
  day: number;
  category: "licht" | "streng";
}

const CHECK_DATES: CheckDate[] = [
  { label: "Lichte toetsing — voorjaar", dateLabel: "19 februari", month: 1, day: 19, category: "licht" },
  { label: "Lichte toetsing — najaar", dateLabel: "21 oktober", month: 9, day: 21, category: "licht" },
  { label: "Strenge toetsing — winter", dateLabel: "19 januari", month: 0, day: 19, category: "streng" },
  { label: "Strenge toetsing — najaar", dateLabel: "22 november", month: 10, day: 22, category: "streng" },
];

export interface HaagseNormCheckResult {
  label: string;
  dateLabel: string;
  category: "licht" | "streng";
  longestSunlitMinutes: number;
  /** Voor deze categorie geldt deze drempel (minuten). */
  requiredMinutes: number;
  passes: boolean;
}

export interface HaagseNormReport {
  blockId: string;
  checks: HaagseNormCheckResult[];
}

/**
 * Beoordeel een specifiek blok t.o.v. de Haagse norm. Het meetpunt is het
 * zwaartepunt van het meetvlak dat bij dit blok hoort (grond voor tuin/
 * bestrating, dak voor gebouwen).
 */
export function evaluateHaagseNormForBlock(scenario: Scenario, block: Block): HaagseNormReport {
  const surfaces = detectMeasurementSurfaces([block]);
  if (surfaces.length === 0) {
    throw new Error("Dit blok heeft geen meetvlak (geen tuin/bestrating en geen dak).");
  }
  // Eerste relevante meetvlak — voor gebouwen is dat het dak; voor
  // tuin/bestrating de grond.
  const surface = surfaces[0];
  const centroid = polygonCentroid(surface.polygon);

  const checks: HaagseNormCheckResult[] = [];
  for (const cd of CHECK_DATES) {
    const longest = longestUninterruptedSunMinutes(
      scenario,
      surface.blockId,
      centroid,
      surface.z,
      cd.month,
      cd.day,
    );
    const required = REQUIRED_MINUTES[cd.category];
    checks.push({
      label: cd.label,
      dateLabel: cd.dateLabel,
      category: cd.category,
      longestSunlitMinutes: longest,
      requiredMinutes: required,
      passes: longest >= required,
    });
  }

  return { blockId: block.id, checks };
}

/**
 * Bereken op een gegeven datum de langste aaneengesloten reeks zonnige
 * minuten op een meetpunt tussen WINDOW_START_MIN en WINDOW_END_MIN.
 *
 * "Zonnig" betekent: zon-elevatie ≥ 10° EN het meetpunt valt niet binnen
 * de schaduwprojectie van een ander blok op het meetvlak-z. Voor de norm
 * tellen we elk schaduwwerkend blok als blokkerend (ook bladdek).
 */
function longestUninterruptedSunMinutes(
  scenario: Scenario,
  ownBlockId: string,
  point: [number, number],
  z: number,
  month: number,
  day: number,
): number {
  let longest = 0;
  let current = 0;
  for (let m = WINDOW_START_MIN; m <= WINDOW_END_MIN; m += SAMPLE_STEP_MIN) {
    const hour = Math.floor(m / 60);
    const minute = m % 60;
    const utc = nlClockToUtc(REFERENCE_YEAR, month, day, hour, minute);
    const sun = getSunPosition(utc, scenario.view.centerLat, scenario.view.centerLng);
    const inSun = sun.altitude >= MIN_ELEVATION_RAD && !isShadowed(scenario, ownBlockId, point, z, sun);
    if (inSun) {
      current += SAMPLE_STEP_MIN;
      if (current > longest) longest = current;
    } else {
      current = 0;
    }
  }
  return longest;
}

function isShadowed(
  scenario: Scenario,
  ownBlockId: string,
  point: [number, number],
  z: number,
  sun: { altitude: number; azimuth: number },
): boolean {
  const shadows = computeShadowsAt(scenario.blocks, sun, z);
  for (const f of shadows) {
    if (f.blockId === ownBlockId) continue;
    if (pointInPolygon(point, f.polygon)) return true;
  }
  return false;
}

function polygonCentroid(polygon: [number, number][]): [number, number] {
  let lng = 0;
  let lat = 0;
  for (const [x, y] of polygon) {
    lng += x;
    lat += y;
  }
  return [lng / polygon.length, lat / polygon.length];
}
